import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useReducer, useState, type FC } from "react";
import type { Database } from "../../../../../../__definitions__/database-definitions";
import type { NoNullFields } from "../../../../../../server/supabase/base-supabase-service";
import { useSupabaseLoader } from "../../../../../../server/supabase/hooks";
import { formatDatetime, fromISO8601 } from "../../../../../../utils/iso8601";
import type { UUID } from "../../../../../../utils/uuid";
import { useSegmentFields } from "../../../../hooks/use-segment-fields";
import type { UsableAccountPersonField } from "../../../../server/supabase-segment-field-service";
import { segmentReducer } from "../../../manager-v2/reducer/reducer";
import { SegmentManager } from "../../../manager-v2/segment-manager";
import { recipientTypeSchema, segmentSchema, type Segment } from "../../../manager-v2/types";

export const SegmentHistory: FC<{ segmentId: UUID }> = ({ segmentId }) => {
  const { loading: segmentFieldsLoading, data: segmentFields } = useSegmentFields();

  const { data, loading, error } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const resp = await supabase
        .from("segment_log")
        .select("*")
        .eq("segment_id", segmentId)
        .eq("account_id", account_id)
        .order("created_at", { ascending: false });
      if (resp.error) {
        throw resp.error;
      }
      return resp.data;
    },
    [segmentId],
  );

  if (loading || segmentFieldsLoading) return <LinearProgress />;
  if (error) {
    console.error(error);
    return <Alert severity="error">Error loading segment history</Alert>;
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell>Action</TableCell>
          <TableCell>User</TableCell>
          <TableCell>Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((log) => (
          <Row key={log.id} log={log} segmentFields={segmentFields ?? []} />
        ))}
      </TableBody>
    </Table>
  );
};

const Row: FC<{
  log: Database["public"]["Tables"]["segment_log"]["Row"];
  segmentFields: NoNullFields<UsableAccountPersonField>[];
}> = ({ log, segmentFields }) => {
  const [open, setOpen] = useState(false);

  const [state, dispatch] = useReducer(segmentReducer, {
    schema: log.is_recipient_type ? recipientTypeSchema : segmentSchema,
    saveState: { saving: false, error: null },
    errors: {
      errors: null,
      display: false,
    },
    segment: {
      id: log.segment_id,
      name: log.name,
      description: log.description ?? "",
      is_default: log.is_default ?? false,
      is_recipient_type: log.is_recipient_type ?? false,
      operation: log.operation as "AND" | "OR",
      segment_json: log.segment_json as Segment["segment_json"],
    },
  });

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            data-analytics-id="segment_history_row_toggle"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{log.action}</TableCell>
        <TableCell>{log.user_name}</TableCell>
        <TableCell>{formatDatetime(fromISO8601(log.created_at))}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Stack direction="column" spacing={2} sx={{ m: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                label="Name"
                required={false}
                value={state.segment.name}
                disabled={true}
              />
              <TextField
                size="small"
                variant="outlined"
                label="Description"
                value={state.segment.description}
                multiline
                disabled={true}
              />
              <SegmentManager
                state={state}
                dispatch={dispatch}
                fields={segmentFields}
                channels={[]}
                disabled={true}
                mode={state.segment.is_recipient_type ? "AUDIENCE" : "SEGMENT"}
                showPreview={false}
              />
            </Stack>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
